import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import JiraConfigPage from "./ConfigPage";
import jira_step_1 from "./images/jira_step_1.png";
import jira_step_2 from "./images/jira_step_2.png";
import jira_step_3 from "./images/jira_step_3.png";
import JiraConnectForm from "./JiraConnectForm";

const JiraIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const jiraConsoleUrl = "https://admin.atlassian.com/";
  const jiraOpenUserUrl =
    "https://admin.atlassian.com/o/a0320782-c452-4ae5-9c2f-5b023301edb9/users?status=ACTIVE";
  const jiraGetTokenUrl =
    "https://id.atlassian.com/manage-profile/security/api-tokens";
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("jira");
  const [loading, setLoading] = useState(false);
  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerJiraIntegration } = useAPI();
  const configFields = {
    "Project name": "autocomplete",
    "Issue type": "autocomplete",
    Assignee: "autocomplete",
    labels: "autocomplete",
    "Include misconfigured entities": "checkbox",
  };

  const onSubmit = (values) => {
    setLoading(true);
    registerJiraIntegration(values.organizationUrl, values.email, values.token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Jira integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          isIntegrated ? "Configure Jira Integration" : "Integrate with Jira"
        }
        image={logos.JiraLogo}
        name={"Jira"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <>
              <JiraConfigPage fields={configFields} />
            </>
          ) : (
            <>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Jira">
                  <Typography variant={"body2"}>
                    Login to your Jira account
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(jiraConsoleUrl, "_blank");
                      }}
                      disabled={guideMode}
                    >
                      Login to Jira Account
                    </PrimaryButton>
                  </Box>
                </GuidelineStep>
                <GuidelineStep title="Create Jira user for integration">
                  <Typography variant={"body2"}>
                    Create a new Jira user or choose an existing Jira user to
                    integrate with the platform.
                  </Typography>
                  <Typography variant={"body2"}>
                    In the email box put the user email e.g{" "}
                    <strong>{"Jira@breeze-security.com"}</strong>
                  </Typography>
                  <Typography variant={"body2"}>
                    Issues will be created on behalf of this user.
                  </Typography>
                  <Typography variant={"body2"}>
                    Make sure the user have project permissions to all relevant
                    projects needed.
                  </Typography>
                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(jiraOpenUserUrl, "_blank");
                      }}
                      disabled={guideMode}
                    >
                      Create a new Jira user
                    </PrimaryButton>
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: jira_step_1,
                        altext: "Jira step 1 create user",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Obtain an API token">
                  <Typography variant={"body2"}>
                    Create an API token for the user, to allow access for the
                    platform.{" "}
                  </Typography>

                  <Box style={{ display: "flex" }}>
                    <PrimaryButton
                      onClick={() => {
                        window.open(jiraGetTokenUrl, "_blank");
                      }}
                      disabled={guideMode}
                    >
                      Create a new API token
                    </PrimaryButton>
                  </Box>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: jira_step_2,
                        altext: "Jira step 2 create token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Give it a meaningful name e.g{" "}
                    <strong>{"Breeze-Jira"}</strong>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Remember to copy and paste it below!
                  </Typography>
                  <GuidelineInstruction text="" />
                  <ScreenshotAccordion
                    images={[
                      {
                        file: jira_step_3,
                        altext: "Jira step 3 copy token",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Connect with API token">
                  <Typography variant={"body2"}>
                    Enter your credentials to connect{" "}
                  </Typography>
                  <JiraConnectForm onSubmit={onSubmit} guideMode={guideMode} />
                </GuidelineStep>
              </GuidelineStepByStep>
            </>
          )
        }
      />
    </Box>
  );
};

export default JiraIntegration;
